import React, { useEffect } from 'react';
import { useTransportationOptions } from '../../API/GoMoto';
import { useAppointmentState } from '../../context/Appointment/appointment';
import { useCart } from '../../context/cart';
import { useKioskImage, useKioskState } from '../../context/Kiosk/kiosk';
import { useLocalization } from '../../context/Localization/localization';
import { useHideKioskBackground, usePageDispatch } from '../../context/Page/page';
import { CTAButton } from '../../controls/ButtonBase';
import { Heading } from '../../controls/Heading/Heading';
import { KioskImageDisplay } from '../../controls/KioskImageDisplay';
import { MobilePath, TransportSubtype } from '../../API/Enums';
import { DisplayModel } from '../../helpers/displayVehicle';
import { useRestartOnBackAfterSignature } from '../../helpers/Hooks';
import { ScrollableBody } from '../Layout/ScrollableBody/ScrollableBody';
import css from './Complete.module.scss';
import { Types } from '../../context/Page/page.types';
import { GMButtonGroup } from '../../controls/ButtonGroup';
import { GMRoundedIcon, GMSizedIcon } from '../../controls/Icon';
import SizedIcon from '../../controls/Icon/SizedIcon/SizedIcon';

export interface CompleteProps {
    /**
     * Passes the resetKiosk for when the user clicks to confirm check-in is complete.
     */
    onComplete: () => void;
}

export const Complete: React.FC<CompleteProps> = React.memo(({ onComplete }) => {
    useHideKioskBackground()();
    useRestartOnBackAfterSignature();
    const Localization = useLocalization();
    const { transportation } = useCart();
    const {
        key: kioskKey,
        kioskType,
        dealer: { key: dealerKey, mobilePath, localization: { fullKioskInstructions } },
    } = useKioskState();

    const { transportationOptions } = useTransportationOptions(kioskKey, dealerKey);
    let closingMessage = '';
    const appointment = useAppointmentState();
    const pageDispatch = usePageDispatch();
    const emailImage = useKioskImage('Email');
    const textImage = useKioskImage('Text');
    const arrowImage = useKioskImage('Arrow');

    if (transportation && transportationOptions) {
        const selected = transportationOptions.find((o) => o.type === transportation.type && o.subtype === (transportation.subtype || TransportSubtype.none));
        closingMessage = selected?.localization.closingMessage || '';
    }

    useEffect(() => {
        if (kioskType !== 'outdoor') {
            pageDispatch({
                type: Types.HideIdleTimeoutDialog,
                payload: true,
            });

            return () => {
                pageDispatch({
                    type: Types.HideIdleTimeoutDialog,
                    payload: false,
                });
            };
        }

        return;
    }, [pageDispatch, kioskType]);

    /*
     * mobilePath only governs the mobile check-in flow. New dealers default to One-Step at
     * the database level (including physical-only dealers), so the advisor-notified
     * suppression is scoped to the mobile kiosk type to avoid hiding the statement on
     * physical kiosks.
     */
    const isMobileOneStep: boolean = kioskType === 'mobile' && mobilePath === MobilePath.OneStep;

    return (
        <ScrollableBody
            heading={
                <Heading
                    roundedIcon={
                        <GMRoundedIcon roundedBackgroundSize={'large'} themeType={'secondary'}>
                            <GMSizedIcon
                                ariaLabel={Localization.CompleteCheckmarkIcon}
                                iconType={'Complete'}
                                iconSize={'extraLarge'}
                            />
                        </GMRoundedIcon>
                    }
                    primaryHeading={Localization.CheckInComplete}
                    secondaryHeading={
                        kioskType === 'outdoor'
                            ? Localization.formatString(Localization.OutdoorCheckInComplete, <b>{appointment.year ?? ''}</b>, <b>{DisplayModel(appointment)}</b>)
                            : undefined
                    }
                    showMustache
                />
            }
            proceedButtons={
                <GMButtonGroup
                    buttons={[
                        <CTAButton
                            styleType={'primary'}
                            onClick={onComplete}
                            title={Localization.ClickToConfirmCheckInComplete}
                            label={Localization.Finish}
                            kioskOptions={{
                                inlineOptions: {
                                    sizedIcon: (
                                        <SizedIcon
                                            iconSize={'custom'}
                                            iconType={'Complete'}
                                            ariaLabel={Localization.CompleteCheckmarkIcon}
                                        />
                                    ),
                                },
                            }}
                            id={Localization.ClickToConfirmCheckInComplete}
                            ariaLabel={Localization.ClickToConfirmCheckInComplete}
                        />,
                    ]}
                />
            }
        >
            <div className={css.closingMessage}>
                {kioskType === 'outdoor' && <div className={css.message}>
                    {fullKioskInstructions}
                </div>}
                <div className={css.message}>
                    {closingMessage}
                </div>
            </div>
            <div className={css.center}>
                {kioskType !== 'outdoor' && (
                    <KioskImageDisplay
                        left={{
                            image: appointment.contactPreference === 'text' ? textImage : emailImage,
                            alt: appointment.contactPreference === 'text' ? Localization.SendTextIcon : Localization.SendEmailIcon,
                        }}
                        right={{
                            top: {
                                text: appointment.contactPreference === 'text' ? Localization.CheckYourTexts : Localization.CheckYourEmail,
                            },
                            image: arrowImage,
                            alt: Localization.ProceedArrow,
                            bottom: isMobileOneStep ? undefined : Localization.ServiceAdvisorNotified,
                        }}
                    />
                )}
            </div>
        </ScrollableBody>
    );
});
